// Local-development stubs for the legacy automation settings page.
// Scratch file for manual testing — not intended to be committed.
//
// The develop backend returns an empty `datapoints` array for most queues,
// which leaves the per-field thresholds table with nothing to render. These
// handlers fill it with rows built against schema 733 (queue 79, "US Demo").
//
// Enable in the browser console, then reload:
//
//   localStorage.setItem('mock_server_enabled', '1');
//   localStorage.setItem('mock_server_scenario', 'queueAutomation');
//
// Scenarios (see mocks/browser.ts):
//   queueAutomation          full table + automation blockers
//   queueAutomationConflict  same, but every schema PATCH returns 412 —
//                            exercises the schemaUpdateConflict toast
//   queueAutomationEmpty     no datapoints, no blockers — the "no data" branch
//
// Turn off with:
//
//   localStorage.removeItem('mock_server_enabled');
//
// NOTE: the page only renders when `mat-317-automation-settings-v2` is OFF.

import { http, HttpResponse, passthrough } from 'msw';

// Matches the develop backend (https://elis.develop.r8.lol/api/v1/...) and a
// local proxy (/api/v1/...) alike. Any queue id, so switching queues still
// returns data — rows whose id is absent from that queue's schema drop out,
// which is itself worth seeing.
const QUEUE_AUTOMATION_URL = /\/api\/v1\/queues\/\d+\/automation(?:\?.*)?$/;
const SCHEMA_URL = /\/api\/v1\/schemas\/\d+(?:\?.*)?$/;

// The response is snake_case on the wire — the api-client camelCases it and
// then validates against `automationResponseSchema`, so every field below is
// required and the shape has to match exactly.
type AutomationDatapointBody = {
  id: string;
  count: number;
  confidence_mean: number;
  automation: number;
};

type AutomationBlockerBody = {
  occurrences: number;
  document_occurrences: number;
  document_occurrences_rel: number;
  id: string | null;
  level: 'annotation' | 'datapoint';
  type: string;
};

type AutomationResponseBody = {
  begin_date: string;
  end_date: string;
  queue: string;
  datapoints: AutomationDatapointBody[];
  automation_blockers: AutomationBlockerBody[];
  totals: {
    document_count: number;
    automation: number;
  };
};

const datapoint = (
  id: string,
  automation: number,
  confidenceMean: number,
  count = 120
): AutomationDatapointBody => ({
  id,
  count,
  confidence_mean: confidenceMean,
  automation,
});

// Ids all exist in schema 733. The set is chosen to cover every shape the
// thresholds table has to path into:
//
//   test_simple_multi  child of a SIMPLE multivalue — `children` is an object,
//                      so the lodash path gets no index hop. The case most
//                      likely to break buildAutomationSchemaMap.
//   item_amount        line-item column: multivalue -> tuple -> children[2],
//                      the deepest path in the schema.
//   my_enum_           already carries score_threshold: 0.0 in the schema
//   sender_name        no score_threshold and no ui_configuration (legacy
//                      field) — must fall back to the queue default
//   date_due, jp-test, enum_required, sender_name  constraints.required: true,
//                      so the label shows the `*`
//   ui_configuration.type coverage: data, formula, manual, reasoning, lookup,
//                      captured, and absent
const DATAPOINTS: AutomationDatapointBody[] = [
  datapoint('my_enum_', 0.94, 0.988), // data, has score_threshold
  datapoint('bflmpsvz', 0.81, 0.932), // formula, has score_threshold
  datapoint('date_due', 0.42, 0.771, 96), // required, no threshold
  datapoint('test_simple_multi', 0.63, 0.845, 74), // simple multivalue child
  datapoint('jp-test', 0.15, 0.688, 51), // required + formula
  datapoint('new_field_etags_test', 0.88, 0.961), // manual
  datapoint('enum_required', 0.55, 0.802), // required + captured
  datapoint('ulkjulkj', 0.71, 0.879), // lookup
  datapoint('sender_name', 0.97, 0.994), // required, legacy field
  datapoint('amount_due', 0.9, 0.973),
  datapoint('tadaaa_reasoning', 0.34, 0.742), // reasoning, has score_threshold
  datapoint('item_amount', 0.68, 0.856, 512), // line-item column (data)
  datapoint('item_quantity', 0.79, 0.918, 512), // line-item column
  datapoint('fdsa', 0.22, 0.705, 38), // last section
];

// Seven blockers, so the "show more" toggle past DISPLAYED_BLOCKERS_COUNT (5)
// is exercised too. `type` is free-form and rendered as upperFirst(snakeToSpaces).
const AUTOMATION_BLOCKERS: AutomationBlockerBody[] = [
  {
    occurrences: 312,
    document_occurrences: 198,
    document_occurrences_rel: 0.258,
    id: 'date_due',
    level: 'datapoint',
    type: 'low_score',
  },
  {
    occurrences: 240,
    document_occurrences: 151,
    document_occurrences_rel: 0.197,
    id: 'jp-test',
    level: 'datapoint',
    type: 'failed_validation',
  },
  {
    occurrences: 187,
    document_occurrences: 122,
    document_occurrences_rel: 0.159,
    id: 'item_amount',
    level: 'datapoint',
    type: 'low_score',
  },
  {
    occurrences: 143,
    document_occurrences: 89,
    document_occurrences_rel: 0.116,
    id: 'test_simple_multi',
    level: 'datapoint',
    type: 'empty_value',
  },
  {
    occurrences: 96,
    document_occurrences: 61,
    document_occurrences_rel: 0.079,
    id: null,
    level: 'annotation',
    type: 'automation_disabled',
  },
  {
    occurrences: 54,
    document_occurrences: 37,
    document_occurrences_rel: 0.048,
    id: 'enum_required',
    level: 'datapoint',
    type: 'low_score',
  },
  {
    occurrences: 21,
    document_occurrences: 14,
    document_occurrences_rel: 0.018,
    id: null,
    level: 'annotation',
    type: 'human_rejected',
  },
];

export const automationResponse = (
  queueId: string,
  { beginDate, endDate }: { beginDate: string; endDate: string },
  overrides?: Partial<AutomationResponseBody>
): AutomationResponseBody => ({
  begin_date: beginDate,
  end_date: endDate,
  queue: `https://elis.develop.r8.lol/api/v1/queues/${queueId}`,
  datapoints: DATAPOINTS,
  automation_blockers: AUTOMATION_BLOCKERS,
  totals: {
    document_count: 768,
    automation: 0.62,
  },
  ...overrides,
});

// The endpoint is a POST carrying { begin_date, end_date }; echo them back so
// the date-range picker stays consistent with what the table claims to show.
const readPayload = async (request: Request) => {
  const body = await request
    .clone()
    .json()
    .catch(() => null);

  return {
    beginDate: body?.begin_date ?? '2026-08-01',
    endDate: body?.end_date ?? '2026-09-02',
  };
};

const queueIdFrom = (url: string) =>
  new URL(url).pathname.match(/\/queues\/(\d+)\/automation/)?.[1] ?? '79';

// Everything else reaches the real backend (including the schema itself), and
// stays out of the "unhandled request" warnings.
const passthroughEverythingElse = http.all('*', () => passthrough());

const automationHandler = (overrides?: Partial<AutomationResponseBody>) =>
  http.post(QUEUE_AUTOMATION_URL, async ({ request }) =>
    HttpResponse.json(
      automationResponse(
        queueIdFrom(request.url),
        await readPayload(request),
        overrides
      )
    )
  );

export const queueAutomationHandlers = [
  automationHandler(),
  passthroughEverythingElse,
];

export const queueAutomationEmptyHandlers = [
  automationHandler({
    datapoints: [],
    automation_blockers: [],
    totals: { document_count: 0, automation: 0 },
  }),
  passthroughEverythingElse,
];

// Rejects every schema PATCH with 412 so the api-client raises HttpError with
// code 412 — the branch that shows the `schemaUpdateConflict` toast.
export const queueAutomationConflictHandlers = [
  automationHandler(),
  http.patch(SCHEMA_URL, () =>
    HttpResponse.json(
      { detail: 'Mocked etag mismatch.' },
      { status: 412, statusText: 'Precondition Failed' }
    )
  ),
  passthroughEverythingElse,
];
